/**
 * s3PathValidator.ts
 *
 * Provides utility functions for validating S3 object keys and prefixes
 * to prevent path traversal, injection attacks, and ensure adherence to S3 naming conventions.
 */
import { S3ServiceError } from '../services/S3Service'; // Corrected import path
// Logger for validation failures (for audit purposes)
const logValidationFailure = (type, value, reason) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
        timestamp,
        type: `s3_path_validation_failure`,
        pathType: type,
        attemptedValue: value.substring(0, 100), // Truncate for security
        reason,
        // In production, you might want to include user ID here
    };
    // Log to console for now - in production, this should go to a proper logging service
    console.warn('[S3 Path Validation Failure]', logEntry);
    // Could also send to a logging service or analytics
    // Example: logToService('security_events', logEntry);
};
const MAX_S3_KEY_LENGTH = 1024; // S3 key max length is 1024 bytes (UTF-8)
/**
 * Validates an S3 object key.
 * Enforces safe characters, prevents path traversal, and checks length.
 * @param key The S3 object key to validate.
 * @returns The validated key.
 * @throws S3ServiceError if the key is invalid.
 */
export function validateS3Key(key) {
    if (typeof key !== 'string' || key.length === 0) {
        logValidationFailure('key', key || '', 'Empty key');
        throw new S3ServiceError('S3 key cannot be empty.', 'INVALID_S3_KEY');
    }
    if (key.length > MAX_S3_KEY_LENGTH) {
        logValidationFailure('key', key, `Exceeds maximum length of ${MAX_S3_KEY_LENGTH}`);
        throw new S3ServiceError(`S3 key exceeds maximum length of ${MAX_S3_KEY_LENGTH} characters.`, 'INVALID_S3_KEY');
    }
    // Prevent path traversal sequences
    if (key.includes('../') || key.includes('./') || key.includes('..\\') || key.includes('.\\')) {
        logValidationFailure('key', key, 'Path traversal sequences detected');
        throw new S3ServiceError('S3 key contains path traversal sequences.', 'INVALID_S3_KEY');
    }
    // Prevent double slashes
    if (key.includes('//')) {
        logValidationFailure('key', key, 'Double slashes detected');
        throw new S3ServiceError('S3 key contains double slashes.', 'INVALID_S3_KEY');
    }
    // Allowed characters: alphanumeric, /, -, _, ., spaces, and common symbols
    // AWS S3 allows these special characters: ! - _ . * ' ( ) @ # $ ^ &
    // This regex is for keys, so it should not allow leading/trailing slashes.
    const keyAllowedCharsRegex = /^[a-zA-Z0-9/\-_\.\s!*'()@#$^&]+$/;
    if (!keyAllowedCharsRegex.test(key)) {
        logValidationFailure('key', key, 'Disallowed characters detected');
        throw new S3ServiceError('S3 key contains disallowed characters.', 'INVALID_S3_KEY');
    }
    // Ensure key does not start or end with a slash
    if (key.startsWith('/') || key.endsWith('/')) {
        logValidationFailure('key', key, 'Key starts or ends with slash');
        throw new S3ServiceError('S3 key cannot start or end with a slash.', 'INVALID_S3_KEY');
    }
    return key;
}
/**
 * Validates an S3 object prefix (folder path).
 * Enforces safe characters, prevents path traversal, and checks length.
 * A valid prefix typically ends with a slash.
 * @param prefix The S3 object prefix to validate.
 * @returns The validated prefix.
 * @throws S3ServiceError if the prefix is invalid.
 */
export function validateS3Prefix(prefix) {
    if (typeof prefix !== 'string') {
        logValidationFailure('prefix', String(prefix), 'Not a string');
        throw new S3ServiceError('S3 prefix must be a string.', 'INVALID_S3_PREFIX');
    }
    if (prefix.length === 0) {
        return ''; // Empty prefix is valid (root of the bucket)
    }
    if (prefix.length > MAX_S3_KEY_LENGTH) {
        logValidationFailure('prefix', prefix, `Exceeds maximum length of ${MAX_S3_KEY_LENGTH}`);
        throw new S3ServiceError(`S3 prefix exceeds maximum length of ${MAX_S3_KEY_LENGTH} characters.`, 'INVALID_S3_PREFIX');
    }
    // Prevent path traversal sequences
    if (prefix.includes('../') || prefix.includes('./') || prefix.includes('..\\') || prefix.includes('.\\')) {
        logValidationFailure('prefix', prefix, 'Path traversal sequences detected');
        throw new S3ServiceError('S3 prefix contains path traversal sequences.', 'INVALID_S3_PREFIX');
    }
    // Prevent double slashes
    if (prefix.includes('//')) {
        logValidationFailure('prefix', prefix, 'Double slashes detected');
        throw new S3ServiceError('S3 prefix contains double slashes.', 'INVALID_S3_PREFIX');
    }
    // Allowed characters for prefixes (similar to keys, but allows trailing slash)
    // AWS S3 allows these special characters: ! - _ . * ' ( ) @ # $ ^ &
    const prefixAllowedCharsRegex = /^[a-zA-Z0-9/\-_\.\s!*'()@#$^&]+$/;
    if (!prefixAllowedCharsRegex.test(prefix)) {
        logValidationFailure('prefix', prefix, 'Disallowed characters detected');
        throw new S3ServiceError('S3 prefix contains disallowed characters.', 'INVALID_S3_PREFIX');
    }
    // A valid prefix should typically end with a slash, unless it's an empty string.
    if (!prefix.endsWith('/')) {
        logValidationFailure('prefix', prefix, 'Prefix does not end with slash');
        throw new S3ServiceError('S3 prefix must end with a slash.', 'INVALID_S3_PREFIX');
    }
    return prefix;
}
